import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Handshake, PlayCircle, RefreshCcw, AlertCircle, CheckCircle2, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HandshakeClassifierPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/biotech-companies/classify-handshake-bulk/status`, { withCredentials: true });
      setStatus(r?.data || null);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Poll every 6 s if a run is in progress.
    const t = setInterval(() => {
      if (status?.status === 'running') fetchStatus();
    }, 6000);
    return () => clearInterval(t);
  }, [status?.status, fetchStatus]);

  const runClassifier = async (onlyUnset = true) => {
    const label = onlyUnset ? 'unset' : 'ALL (including already-classified)';
    if (!window.confirm(`Run AI handshake classifier over ${label} biotechs?\n\nClaude will read each company's profile and decide whether they in-license. Sets is_buyer_eligible True/False.\n\n${onlyUnset ? 'Manual toggles are preserved.' : '⚠️ This RE-classifies every biotech and can overwrite AI results (but never manual toggles).'}\n\nRuns in the background (~15 min for 1,896 biotechs).`)) return;
    try {
      setStarting(true);
      const r = await axios.post(`${API}/biotech-companies/classify-handshake-bulk?only_unset=${onlyUnset}`, {}, { withCredentials: true });
      toast.success(`🤖 Classifier started (job ${r?.data?.job_id || 'handshake_classifier'}). Poll progress on this page.`);
      setTimeout(fetchStatus, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start handshake classifier.');
    } finally {
      setStarting(false);
    }
  };

  const s = status || {};
  const total = s.total || 0;
  const processed = s.processed || 0;
  const eligible = s.eligible || 0;
  const ineligible = s.ineligible || 0;
  const errors = s.errors || 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const running = s.status === 'running';
  const completed = s.status === 'completed';
  const failed = s.status === 'failed';
  const stalled = s.status === 'stalled';
  const cancelled = s.status === 'cancelled';
  // A stalled/cancelled/failed job should NOT block the Re-classify
  // buttons — the worker is dead and the user needs to be able to
  // relaunch. Only a currently-running job with a fresh heartbeat locks
  // the buttons.
  const blockActions = starting || running;

  const cancelStuckJob = async () => {
    if (!window.confirm('Force-cancel this stuck job? The 312 biotechs already classified will keep their AI results — you can restart the classifier immediately after this.')) return;
    try {
      await axios.post(`${API}/biotech-companies/classify-handshake-bulk/reset`, {}, { withCredentials: true });
      toast.success('Stuck job cleared. You can restart the classifier now.');
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reset stuck job.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6" data-testid="handshake-classifier-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Handshake className="w-6 h-6 text-amber-500" /> Handshake Classifier
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Uses Claude Sonnet 4.5 to read each biotech&apos;s profile (stage, deal interests, TA, description, recent deals) and decide whether they in-license assets. The result becomes <code className="text-xs bg-slate-100 px-1 rounded">is_buyer_eligible</code>, which the AI Mapping and Send guards use as a HARD eligibility filter.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <TrendingUp className="w-4 h-4 text-slate-500" /> Current status
            <Button variant="ghost" size="sm" onClick={fetchStatus} disabled={loading} data-testid="refresh-status-btn" className="ml-auto text-slate-500 hover:text-slate-700 h-7 w-7 p-0">
              <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
          {!status && (
            <CardDescription className="text-xs text-slate-400 italic">
              Loading…
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            {running && <Badge className="bg-sky-100 text-sky-700 border-sky-200">RUNNING</Badge>}
            {completed && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">COMPLETED</Badge>}
            {failed && <Badge className="bg-rose-100 text-rose-700 border-rose-200">FAILED</Badge>}
            {stalled && <Badge className="bg-amber-100 text-amber-800 border-amber-300">STALLED · worker died</Badge>}
            {cancelled && <Badge className="bg-slate-200 text-slate-700 border-slate-300">CANCELLED</Badge>}
            {!s.status && <Badge variant="outline" className="text-slate-500">IDLE — never run</Badge>}
            {s.status === 'idle' && <Badge variant="outline" className="text-slate-500">IDLE — never run</Badge>}
          </div>

          {stalled && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/60 p-3">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-800 leading-relaxed">
                <p className="font-semibold">Worker died mid-run — most commonly after a redeploy killed the background task.</p>
                <p className="mt-1">
                  {processed}/{total} biotechs were already classified before the crash — their results are preserved in the DB.
                  Click <strong>Cancel stuck job</strong> then <strong>Classify unset biotechs</strong> to pick up right where it left off (only the remaining {Math.max(0, total - processed)} unset biotechs will be re-processed).
                </p>
                {s.stall_reason && <p className="mt-1 italic text-amber-700">{s.stall_reason}</p>}
              </div>
            </div>
          )}

          {total > 0 && (
            <>
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Progress</span>
                  <span className="tabular-nums">{processed} / {total} ({pct}%)</span>
                </div>
                <Progress value={pct} className="h-2" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border border-emerald-100 bg-emerald-50/30 p-3">
                  <p className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">In-licensers</p>
                  <p className="text-xl font-semibold text-emerald-700 tabular-nums mt-1">{eligible}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Out-only / not eligible</p>
                  <p className="text-xl font-semibold text-slate-700 tabular-nums mt-1">{ineligible}</p>
                </div>
                <div className="rounded-md border border-rose-100 bg-rose-50/30 p-3">
                  <p className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider">Errors</p>
                  <p className="text-xl font-semibold text-rose-700 tabular-nums mt-1">{errors}</p>
                </div>
              </div>
            </>
          )}

          {failed && s.error && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50/50 p-3">
              <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5" />
              <p className="text-xs text-rose-700 leading-relaxed"><span className="font-semibold">Error:</span> {s.error}</p>
            </div>
          )}
          {completed && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50/50 p-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
              <p className="text-xs text-emerald-700 leading-relaxed">
                Classification complete. Each biotech now has <code className="bg-white px-1 rounded">is_buyer_eligible</code>, <code className="bg-white px-1 rounded">buyer_eligibility_confidence</code>, and an <code className="bg-white px-1 rounded">buyer_eligibility_evidence</code> field. AI Mapping will now respect these values as a hard filter.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={() => runClassifier(true)}
              disabled={blockActions}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="run-only-unset-btn"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              {running ? 'Running…' : 'Classify unset biotechs'}
            </Button>
            <Button
              onClick={() => runClassifier(false)}
              disabled={blockActions}
              variant="outline"
              className="text-xs border-slate-300 text-slate-600 hover:bg-slate-50"
              data-testid="run-all-btn"
              title="Re-classify all biotechs, INCLUDING those already classified by a previous AI run. Manual is_buyer_eligible toggles set by a human are still preserved server-side."
            >
              Re-classify ALL (overwrite AI results)
            </Button>
            {(stalled || (running && s.last_heartbeat_at)) && (
              <Button
                onClick={cancelStuckJob}
                variant="outline"
                className="text-xs border-rose-300 text-rose-600 hover:bg-rose-50"
                data-testid="cancel-stuck-btn"
                title="Force-cancel the stuck job so the buttons above become clickable."
              >
                Cancel stuck job
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-slate-400 space-y-1 leading-relaxed">
        <p><strong>How it works:</strong> Claude reads name, country, stage, categories, declared deal interests, therapeutic areas, description, and recent deal summary for each biotech, then outputs <code>{'{'}in_licenses, confidence, evidence{'}'}</code>. The result is written to <code>is_buyer_eligible</code> with an audit trail (source, confidence, evidence, timestamp).</p>
        <p><strong>Concurrency:</strong> 6 parallel Claude calls, batch size 24. ~15 min for 1,896 biotechs. Runs as a background asyncio task — this page can be closed safely, progress is persisted server-side.</p>
        <p><strong>Manual overrides:</strong> Never touched. If you flip a biotech&apos;s handshake toggle by hand on their profile page, that decision is respected by both classifier modes.</p>
      </div>
    </div>
  );
}
